import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Edit2, FileText, Plus, Trash2, Truck, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { vehicleService } from '../services/vehicleService';
import { documentService } from '../services/documentService';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { DriverBottomNav } from '../components/DriverBottomNav';

const DOC_TYPES = [
  { key: 'tractor-license', label: 'Çekici ruhsat' },
  { key: 'trailer-license', label: 'Dorse ruhsat' },
  { key: 'tractor-inspection', label: 'Çekici muayene belgesi' },
  { key: 'trailer-inspection', label: 'Dorse muayene belgesi' },
  { key: 'vehicle-insurance', label: 'Araç sigortası' },
];

export function DriverMobileCorporateVehicles() {
  const navigate = useNavigate();
  const user = getStoredDriverMobileUser<any>();
  const carrierId = String(user?.externalTransportAccountUserId || user?.external_transport_account_user_id || user?.id || '');
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState({ cekici_plaka: '', dorse_plaka: '', vehicle_type: '', brand: '', model: '' });

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const response: any = await vehicleService.getVehicles({ ownerType: 'external', externalTransportAccountUserId: carrierId });
      setVehicles(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Corporate mobile vehicles load failed:', error);
      toast.error('Çekiciler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    void loadVehicles();
  }, [carrierId]);

  const saveVehicle = async () => {
    if (!form.cekici_plaka.trim()) {
      toast.warning('Çekici plaka zorunludur');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        cekici_plaka: form.cekici_plaka.toLocaleUpperCase('tr-TR').replace(/\s+/g, ''),
        dorse_plaka: form.dorse_plaka.toLocaleUpperCase('tr-TR').replace(/\s+/g, ''),
        vehicle_type: form.vehicle_type.toLocaleUpperCase('tr-TR'),
        owner_type: 'external',
        external_transport_account_user_id: carrierId,
        status: 'active',
      };
      if (editingVehicle?.id) await vehicleService.updateVehicle(editingVehicle.id, payload);
      else await vehicleService.createVehicle(payload);
      setForm({ cekici_plaka: '', dorse_plaka: '', vehicle_type: '', brand: '', model: '' });
      setEditingVehicle(null);
      await loadVehicles();
      toast.success(editingVehicle ? 'Çekici güncellendi' : 'Çekici kaydedildi');
    } catch (error: any) {
      toast.error(error?.message || 'Çekici kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (vehicle: any) => {
    setEditingVehicle(vehicle);
    setForm({
      cekici_plaka: vehicle.cekici_plaka || vehicle.plate || '',
      dorse_plaka: vehicle.dorse_plaka || vehicle.trailer_plate || '',
      vehicle_type: vehicle.vehicle_type || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingVehicle(null);
    setForm({ cekici_plaka: '', dorse_plaka: '', vehicle_type: '', brand: '', model: '' });
  };

  const deleteVehicle = async (vehicle: any) => {
    if (!window.confirm(`${vehicle.cekici_plaka || vehicle.plate || 'Çekici'} silinsin mi?`)) return;
    try {
      setDeletingId(String(vehicle.id));
      await vehicleService.deleteVehicle(vehicle.id);
      if (editingVehicle?.id === vehicle.id) cancelEdit();
      await loadVehicles();
      toast.success('Çekici silindi');
    } catch (error: any) {
      toast.error(error?.message || 'Çekici silinemedi');
    } finally {
      setDeletingId(null);
    }
  };

  const uploadDocument = async (vehicle: any, documentType: string, file?: File | null) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'vehicle_document');
      formData.append('entityId', String(vehicle.id));
      formData.append('documentType', documentType);
      formData.append('name', DOC_TYPES.find((item) => item.key === documentType)?.label || documentType);
      await documentService.uploadDocument(formData);
      toast.success('Belge yüklendi');
    } catch (error: any) {
      toast.error(error?.message || 'Belge yüklenemedi');
    }
  };

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-4 shadow-sm">
        <button onClick={() => navigate('/driver-mobile-profile')} className="mb-3 inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 font-semibold text-slate-900"><ArrowLeft className="h-5 w-5" /> Geri</button>
        <h1 className="text-xl font-bold text-slate-900">Çekiciler</h1>
        <p className="text-sm text-slate-500">Çekici/dorse bilgisi ekleyin ve araç belgelerini yükleyin.</p>
      </div>

      <div className="space-y-4 p-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2 font-bold text-slate-900">
            <span className="inline-flex items-center gap-2">{editingVehicle ? <Edit2 className="h-5 w-5" /> : <Plus className="h-5 w-5" />} {editingVehicle ? 'Çekici Düzenle' : 'Yeni Çekici'}</span>
            {editingVehicle ? <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-700"><X className="h-3.5 w-3.5" /> Vazgeç</button> : null}
          </div>
          <div className="grid gap-3">
            <input value={form.cekici_plaka} onChange={(e) => setForm({ ...form, cekici_plaka: e.target.value })} placeholder="Çekici plaka" className="rounded-xl border border-slate-300 px-3 py-3" />
            <input value={form.dorse_plaka} onChange={(e) => setForm({ ...form, dorse_plaka: e.target.value })} placeholder="Dorse plaka" className="rounded-xl border border-slate-300 px-3 py-3" />
            <input value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} placeholder="Araç tipi" className="rounded-xl border border-slate-300 px-3 py-3" />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Marka" className="rounded-xl border border-slate-300 px-3 py-3" />
              <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Model" className="rounded-xl border border-slate-300 px-3 py-3" />
            </div>
            <button disabled={saving} onClick={saveVehicle} className="rounded-xl bg-blue-600 px-4 py-3 font-bold text-white disabled:opacity-60">{editingVehicle ? 'Güncelle' : 'Kaydet'}</button>
          </div>
        </div>

        {loading ? <div className="rounded-xl bg-white p-6 text-center text-slate-500">Yükleniyor...</div> : vehicles.map((vehicle) => (
          <div key={vehicle.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <Truck className="mt-1 h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-bold text-slate-900">{vehicle.cekici_plaka || vehicle.plate || '-'}</div>
                  <div className="text-sm text-slate-500">Dorse: {vehicle.dorse_plaka || vehicle.trailer_plate || '-'} · {vehicle.vehicle_type || '-'}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => startEdit(vehicle)} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700"><Edit2 className="h-4 w-4" /></button>
                <button type="button" disabled={deletingId === String(vehicle.id)} onClick={() => void deleteVehicle(vehicle)} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 disabled:opacity-60"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-4 grid gap-2">
              {DOC_TYPES.map((doc) => (
                <label key={doc.key} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">
                  <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4" /> {doc.label}</span>
                  <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs"><Upload className="h-3.5 w-3.5" /> Yükle</span>
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => void uploadDocument(vehicle, doc.key, e.target.files?.[0])} />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <DriverBottomNav />
    </div>
  );
}
